import { ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { BanksService } from '../../../services/banks.service';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { WorkOrdersService } from '../../../services/work-orders.service';
import { ToastService } from '../../../services/toast.service';
import { ExpenseVouchersService } from '../../../services/expense-vouchers.service';
import { forkJoin } from 'rxjs';
import { LoadingService } from '../../../services/loading.service';
import { Router } from '@angular/router';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { PaymentMethodService } from '../../../services/payment-method.service';
import { FeeTypesService } from '../../../services/fee-types.service';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { ActivatedRoute } from '@angular/router';
import { CurrencyService, CurrencyResponseDTO } from '../../../services/currency.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';
import { PaymentPlansService } from '../../../services/payment-plans.service';
import { CompanyService } from '../../../services/company.service';
import { CorporateEnrollmentService } from '../../../services/corporate-enrollment.service';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component'; // 🆕 Agregado
import { tap, switchMap } from 'rxjs/operators'; // 🆕 Importar operadores RxJS


@Component({
  selector: 'app-new-income-receipt',
  templateUrl: './new-income-receipt.component.html',
  styleUrl: './new-income-receipt.component.scss'
})
export class NewIncomeReceiptComponent extends BaseFormComponent {

  incomeReceiptForm!: FormGroup;
  banks: any = []; // Aquí cargarías los bancos disponibles
  participants: any = [];
  projects: any = [];
  enrollmentsByStudent: any = [];
  paymentsPlans: any = [];
  planAllocations: any = [];
  payerSelection: 'COMPANY' | 'PARTICIPANT' | null = null;
  paymentMethods: any = [];
  filteredPaymentMethods: any = [];
  feeTypes: any = [];
  incomeProjectBudgets: any = [];

  // 🆕 PROPIEDADES DEL WIZARD
  step: number = 1;
  totalSteps: number = 3; // Se cambiará a 4 dinámicamente cuando se genere un link

  // 🆕 PROPIEDADES DEL PAGADOR (Principio SRP)
  companies: any[] = [];
  selectedPayerType: 'PARTICIPANT' | 'COMPANY' | 'PARTICIPANT_CORPORATE' = 'PARTICIPANT';
  selectedCompanyId: number | null = null;

  // 🆕 ENUM PARA USAR EN EL TEMPLATE
  PayerType = {
    PARTICIPANT: 'PARTICIPANT' as const,
    COMPANY: 'COMPANY' as const,
    PARTICIPANT_CORPORATE: 'PARTICIPANT_CORPORATE' as const
  };

  // Variables para manejo de inscripciones internacionales
  selectedEnrollmentInfo: any = null;
  copEquivalent: number = 0;
  currentExchangeRate: number = 1;

  // 🆕 PROPIEDADES PARA MANEJO DEL LINK GENERADO
  generatedPaymentLink: any = null;
  isLinkGenerated: boolean = false;
  showSuccessStep: boolean = false;

  // Propiedades para pantalla de éxito de comprobantes generados
  creationResult: any = null;
  showCreationResult: boolean = false;

  // Tracking de validación de inscripciones
  participantHasEnrollments: boolean | null = null;
  participantEnrollmentCheckMessage: string = '';
  companyHasEnrollments: boolean | null = null;
  companyEnrollmentCheckMessage: string = '';

  conceptOptions = [
    { label: 'Matrícula', value: 'Matricula' },
    { label: 'Cuota Cartera', value: 'Cuota Cartera' },
    { label: 'Otro', value: 'Otro' }
  ];

  readonly financiableConcepts: string[] = [
    'Matricula',
    'Cuota Cartera'
  ];

  // 🆕 PROPIEDADES PARA MODO EDICIÓN
  isEditMode = false;
  editFromId: number | null = null;
  editJustification = '';

  public workOrders: any = [];
  constructor(
    private fb: FormBuilder,
    router: Router,
    private _banksService: BanksService,
    private _participantsService: ParticipantService,
    private _enrollmentService: EnrollmentService,
    private _projectService: ProjectService,
    private toastService: ToastService,
    private _incomeReceiptService: IncomeReceiptsService,
    private _paymentMethodService: PaymentMethodService,
    private _feeTypesService: FeeTypesService,
    private _loadingService: LoadingService,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private _currencyService: CurrencyService,
    private _exchangeRateService: ExchangeRateService,
    private _paymentPlansService: PaymentPlansService,
    private _companyService: CompanyService,
    private _corporateEnrollmentService: CorporateEnrollmentService, // 🆕 Agregado
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    // El formulario se inicializa en ngOnInit
  }

  protected override getMainForm(): FormGroup {
    return this.incomeReceiptForm;
  }

  public override getFormName(): string {
    return 'comprobante de ingreso';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    // Información del pagador
    if (this.selectedPayerType === 'PARTICIPANT' && this.getSelectedParticipant()) {
      const participant = this.getSelectedParticipant();
      data.push(`Participante: ${participant.firstName} ${participant.lastName}`);
    } else if (this.selectedPayerType === 'COMPANY' && this.getSelectedCompany()) {
      const company = this.getSelectedCompany();
      data.push(`Empresa: ${company.name}`);
    }

    // Monto y concepto
    const grossAmount = this.incomeReceiptForm.get('grossAmount')?.value;
    if (grossAmount) {
      data.push(`Monto: ${this.getFormattedGrossAmount()}`);
    }

    const concept = this.incomeReceiptForm.get('concept')?.value;
    if (concept) {
      data.push(`Concepto: ${this.getSelectedConceptLabel()}`);
    }

    // Si es link de pago
    if (this.isLinkPayment()) {
      data.push('Tipo: Enlace de pago');
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    await this.submitForm();
  }

  protected override navigateBack(): void {
    this.router.navigate(['/dashboard/income-receipts']);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // this.loadInitialData(); // 🆕 Ahora se llama dentro del subscribe de queryParams o loadReceiptForEditing

    this.incomeReceiptForm = this.fb.group({
      paymentPlanId: [0, this.conditionalValidator(
        () => this.incomeReceiptForm?.get('payerType')?.value === 'PARTICIPANT' && this.isWompiPaymentMethod(),
        Validators.min(1)
      )],
      paymentMethodId: [0, Validators.required],
      participantId: [0, Validators.required],
      bankId: ['', this.conditionalValidator(() => this.incomeReceiptForm?.get('PaymentMethodId')?.value === "BANK_TRANSFER", Validators.required)],
      grossAmount: [0, Validators.required],
      projectId: [8, Validators.required],
      projectBudgetId: [null, Validators.required],
      currencyId: [1, Validators.required],
      exchangeRate: [0, Validators.required],
      referenceNumber: [''],
      concept: ['', Validators.required],
      observations: [''],
      feeDetails: this.fb.array([]),

      transactionDate: [new Date(), Validators.required], // 🆕 Campo Fecha de Pago
      // 🆕 NUEVOS CAMPOS DEL PAGADOR
      payerType: ['PARTICIPANT', Validators.required],
      companyId: [null] // Requerido solo cuando payerType === 'COMPANY'
    });


    // 🧠 Capturar parámetros de la URL
    this.route.queryParams.subscribe(params => {
      const participantId = params['participantId'];
      const projectId = params['projectId'];
      const installmentNumber = params['installmentNumber'];
      const companyId = params['companyId'];
      const isCompanyPayment = params['isCompanyPayment'];
      const editFromId = params['editFromId'];

      console.log('🔗 Parámetros URL recibidos:', { participantId, projectId, installmentNumber, companyId, isCompanyPayment, editFromId });

      // 🆕 MODO EDICIÓN: cargar los datos del comprobante original
      if (editFromId) {
        this.isEditMode = true;
        this.editFromId = Number(editFromId);
        this.loadReceiptForEditing(this.editFromId);
        return; // No procesar otros params en modo edición
      }

      // 🔄 MODO CREACIÓN NORMAL: Cargar data inicial y luego procesar params
      this.loadInitialData().subscribe(() => {
        // 🔄 CORRECCIÓN: Determinar tipo de pagador basado en parámetros
        const targetSelectionId = Number(params['corporateEnrollmentId'] || params['enrollmentId'] || projectId);

        if (isCompanyPayment === 'true' && companyId) {
          this.selectedPayerType = 'COMPANY';
          this.selectedCompanyId = Number(companyId);
          this.incomeReceiptForm.patchValue({
            payerType: 'COMPANY',
            companyId: Number(companyId)
          });
          this.initializeWithUrlParams(targetSelectionId, installmentNumber, 'company');
        } else if (companyId && participantId) {
          this.selectedPayerType = 'PARTICIPANT_CORPORATE';
          this.selectedCompanyId = Number(companyId);
          this.incomeReceiptForm.patchValue({
            payerType: 'PARTICIPANT_CORPORATE',
            participantId: Number(participantId),
            companyId: Number(companyId)
          });
          this.initializeWithUrlParams(targetSelectionId, installmentNumber, 'participant_corporate');
        } else if (participantId) {
          this.selectedPayerType = 'PARTICIPANT';
          this.incomeReceiptForm.patchValue({
            payerType: 'PARTICIPANT',
            participantId: Number(participantId)
          });
          this.initializeWithUrlParams(targetSelectionId, installmentNumber, 'individual');
        }
      });
    });

  }


  get feeDetails(): FormArray {
    return this.incomeReceiptForm?.get('feeDetails') as FormArray || this.fb.array([]);
  }

  addFeeDetail() {
    const feeGroup = this.fb.group({
      feeTypeId: ['', Validators.required],
      requestAmount: ['', [Validators.required, Validators.min(1)]],
      description: ['']
    });

    this.feeDetails.push(feeGroup);
  }

  // Eliminar un Fee
  removeFeeDetail(index: number) {
    this.feeDetails.removeAt(index);
  }

  conditionalValidator(predicate: () => boolean, validator: any) {
    return (control: AbstractControl) => {
      if (!control || !control.parent) {
        return null;
      }

      return predicate() ? validator(control) : null;
    };
  }

  loadInitialData() {
    this._loadingService.show();
    return forkJoin({
      participants: this._participantsService.getAll(),
      banks: this._banksService.getAll(),
      projects: this._projectService.getLookup(),   // 🆕 antes: getAll()
      paymentMethods: this._paymentMethodService.getAll(),
      feeTypes: this._feeTypesService.getAll(),
      companies: this._companyService.getAll() // 🆕 Cargar empresas
    }).pipe(
      tap({
        next: (results: any) => {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const participantsData = Array.isArray(results.participants.data) ? results.participants.data : (results.participants.data?.data || []);
          const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
          const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
          const paymentMethodsData = Array.isArray(results.paymentMethods.data) ? results.paymentMethods.data : (results.paymentMethods.data?.data || []);
          // feeTypes siempre devuelve un array directamente en data
          const feeTypesData = results.feeTypes.data || [];
          const companiesData = Array.isArray(results.companies.data) ? results.companies.data : (results.companies.data?.data || []);

          this.participants = participantsData.map((data: any) => ({ label: `${data.typeDocument} ${data.documentId} ${data.firstName} ${data.lastName}`, value: data.id }));
          this.banks = banksData.map((data: any) => ({ label: data.bankName, value: data.id }));
          this.projects = projectsData;
          this.paymentMethods = paymentMethodsData.map((data: any) => ({ label: data.name, value: data.id, data: data }));
          this.filteredPaymentMethods = [...this.paymentMethods]; // Inicialmente todos disponibles
          this.feeTypes = feeTypesData;
          this.companies = companiesData.map((c: any) => ({ label: `${c.name} (${c.documentId})`, value: c.id, data: c })); // 🆕 Mapear empresas
          this._loadingService.hide();
        },
        error: (err) => {
          console.error('Error loading initial data', err);
          this._loadingService.hide();
        }
      })
    );
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._participantsService.getAll().subscribe({
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.participants = data.map((d: any) => ({
            label: `${d.typeDocument} ${d.documentId} ${d.firstName} ${d.lastName}`,
            value: d.id
          }));
        },
        error: (err) => console.error('Error loading participants:', err)
      });
      return;
    }

    this._participantsService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.participants = data.map((d: any) => ({
          label: `${d.typeDocument} ${d.documentId} ${d.firstName} ${d.lastName}`,
          value: d.id
        }));
      },
      error: (err) => console.error('Error searching participants:', err)
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._companyService.getAll().subscribe({
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.companies = data.map((c: any) => ({
            label: `${c.name} (${c.documentId})`,
            value: c.id,
            data: c
          }));
        },
        error: (err) => console.error('Error loading companies:', err)
      });
      return;
    }

    this._companyService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.companies = data.map((c: any) => ({
          label: `${c.name} (${c.documentId})`,
          value: c.id,
          data: c
        }));
      },
      error: (err) => console.error('Error searching companies:', err)
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._projectService.getLookup().subscribe({   // 🆕 antes: getAll()
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.projects = data;
        },
        error: (err) => console.error('Error loading projects:', err)
      });
      return;
    }

    this._projectService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.projects = data;
      },
      error: (err) => console.error('Error searching projects:', err)
    });
  }

  shouldShowPaymentPlan(): boolean {
    if (this.isEditMode) {
      return false;
    }

    const conceptValue = this.incomeReceiptForm.get('concept')?.value;

    if (typeof conceptValue === 'string') {
      return this.financiableConcepts.includes(conceptValue);
    }

    if (conceptValue && typeof conceptValue.value === 'string') {
      return this.financiableConcepts.includes(conceptValue.value);
    }

    return false;
  }

  isWompiPaymentMethod(): boolean {
    const paymentMethodId = this.incomeReceiptForm.get('paymentMethodId')?.value;
    if (!paymentMethodId) return false;
    const selectedMethod = this.paymentMethods.find((pm: any) => pm.value === paymentMethodId);
    return selectedMethod && selectedMethod.label.toLowerCase().includes('wompi');
  }

  /**
   * 🆕 Revalida paymentPlanId cuando cambia el método de pago, porque su
   * validador condicional depende de isWompiPaymentMethod() y Angular no
   * re-evalúa automáticamente el validador de un control cuando cambia un
   * sibling control (paymentMethodId) del mismo FormGroup.
   */
  onPaymentMethodChange(): void {
    this.incomeReceiptForm.get('paymentPlanId')?.updateValueAndValidity();
  }


  // 🗑️ MÉTODO ELIMINADO: getAllEnrollmentsByStudent - Reemplazado por loadIndividualEnrollments

  getAllPaymentPlansByProject(projectId?: number) {
    console.log("🔄 Get payment plans for projectId:", projectId, "PayerType:", this.selectedPayerType);

    if (!projectId) {
      console.warn('⚠️ No projectId provided');
      return;
    }

    // 🆕 LÓGICA DIFERENCIADA SEGÚN TIPO DE PAGADOR
    switch (this.selectedPayerType) {
      case this.PayerType.PARTICIPANT:
        this.loadIndividualPaymentPlans(projectId);
        break;

      case this.PayerType.COMPANY:
        this.loadCompanyPaymentPlans(projectId);
        break;

      case this.PayerType.PARTICIPANT_CORPORATE:
        this.loadParticipantCorporatePaymentPlans(projectId);
        break;

      default:
        console.warn('⚠️ Tipo de pagador no reconocido:', this.selectedPayerType);
    }
  }

  /**
   * 🆕 Carga payment plans para inscripciones individuales
   */
  private loadIndividualPaymentPlans(projectId: number): void {
    console.log('🔍 === INICIANDO CARGA DE PAYMENT PLANS INDIVIDUALES ===');
    console.log('🔍 ProjectId recibido:', projectId);
    console.log('🔍 this.projects disponibles:', this.projects);

    const selectedProject = this.projects.find((p: any) => p.value === projectId);
    const selectedProjectId = selectedProject?.projectId || projectId;
    const enrollmentId = selectedProject?.enrollmentId;

    console.log('🔍 Proyecto encontrado:', selectedProject);
    console.log('🔍 EnrollmentId extraído:', enrollmentId);
    console.log('🔍 Propiedades del proyecto:', selectedProject ? Object.keys(selectedProject) : 'Proyecto no encontrado');

    if (!enrollmentId) {
      console.error('❌ === ERROR: NO SE ENCONTRÓ ENROLLMENTID ===');
      console.error('❌ ProjectId (dropdown value) buscado:', projectId);
      console.error('❌ Proyectos disponibles detallados:');
      this.projects.forEach((p: any, index: number) => {
        console.error(`❌   [${index}] value: ${p.value}, enrollmentId: ${p.enrollmentId}, label: ${p.label}`);
      });
      this.toastService.showToast('error', 'No se encontró la inscripción para este proyecto');
      return;
    }

    console.log('📚 === LLAMANDO API DE PAYMENT PLANS ===');
    console.log('📚 EnrollmentId para API:', enrollmentId);
    console.log('📚 URL será:', `PaymentPlans/enrollment/${enrollmentId}/pending`);
    this._loadingService.show();

    this._paymentPlansService.getPendingPaymentsByEnrollment(enrollmentId).subscribe({
      next: (response: any) => {
        console.log('📚 === RESPONSE DE PAYMENT PLANS API ===');
        console.log('📚 Response completo:', response);
        console.log('📚 Response.data:', response.data);
        console.log('📚 Tipo de response.data:', typeof response.data);
        console.log('📚 Es array?:', Array.isArray(response.data));

        if (response.data && response.data.length > 0) {
          console.log('✅ Payment plans encontrados:', response.data.length);
          console.log('✅ Primer payment plan:', response.data[0]);
          this.mapPaymentPlansToDropdown(response.data, 'individual');
          console.log('✅ paymentsPlans después del mapeo:', this.paymentsPlans);
          this.toastService.showToast('success', `${response.data.length} cuotas cargadas correctamente`);
        } else {
          console.warn('⚠️ No se encontraron payment plans pendientes');
          console.warn('⚠️ Response data:', response.data);
          this.paymentsPlans = [];
          this.toastService.showToast('warning', 'No hay cuotas pendientes para este proyecto');
        }
        this._loadingService.hide();
      },
      error: (error: any) => {
        console.error('❌ === ERROR EN API DE PAYMENT PLANS ===');
        console.error('❌ Error completo:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error url:', error.url);
        this.paymentsPlans = [];
        this.toastService.showToast('error', 'Error cargando cuotas de pago: ' + (error.message || 'Error desconocido'));
        this._loadingService.hide();
      }
    });
  }

  /**
   * 🆕 Carga payment plans para empresa
   */
  private loadCompanyPaymentPlans(projectId: number): void {
    const selectedProject = this.projects.find((p: any) => p.value === projectId);
    const corporateEnrollmentId = selectedProject?.corporateEnrollmentId;

    if (!corporateEnrollmentId) {
      console.error('❌ No se encontró corporateEnrollmentId para el proyecto:', projectId);
      return;
    }

    // 🆕 Establecer información de moneda de la inscripción corporativa
    if (selectedProject) {
      this.selectedEnrollmentInfo = {
        currencyId: selectedProject.enrollmentCurrencyId,
        currencyCode: selectedProject.enrollmentCurrencyCode || 'COP',
        currencySymbol: selectedProject.enrollmentCurrencySymbol || '$',
        totalAmount: 0,
        exchangeRate: selectedProject.enrollmentExchangeRate || 1
      };

      // Si no es COP, obtener tasa de cambio actual
      if (this.selectedEnrollmentInfo.currencyCode !== 'COP') {
        this.currentExchangeRate = this.selectedEnrollmentInfo.exchangeRate;
        this.getCurrentExchangeRate();
      } else {
        this.currentExchangeRate = 1;
      }
    }

    console.log('🏢 Cargando payment plans de empresa para corporate enrollment:', corporateEnrollmentId);
    this._loadingService.show();

    this._paymentPlansService.getPendingCompanyPaymentPlans(corporateEnrollmentId).subscribe({
      next: (response: any) => {
        console.log('✅ Payment plans de empresa cargados:', response.data);
        // 🆕 Mapear planes inmediatamente, usar currentExchangeRate (se actualizará cuando llegue la respuesta de getCurrentExchangeRate)
        this.mapPaymentPlansToDropdown(response.data, 'company');
        this._loadingService.hide();
      },
      error: (error: any) => {
        console.error('❌ Error cargando payment plans de empresa:', error);
        this.toastService.showToast('error', 'Error cargando cuotas de pago de la empresa');
        this._loadingService.hide();
      }
    });
  }

  /**
   * 🆕 Carga payment plans para participante corporativo
   */
  private loadParticipantCorporatePaymentPlans(projectId: number): void {
    const selectedProject = this.projects.find((p: any) => p.value === projectId);
    const corporateEnrollmentId = selectedProject?.corporateEnrollmentId;
    const participantId = this.incomeReceiptForm.get('participantId')?.value;

    if (!corporateEnrollmentId) {
      console.error('❌ No se encontró corporateEnrollmentId para el proyecto:', projectId);
      return;
    }

    if (!participantId) {
      console.error('❌ No se encontró participantId');
      return;
    }

    // 🆕 Establecer información de moneda de la inscripción corporativa
    if (selectedProject) {
      this.selectedEnrollmentInfo = {
        currencyId: selectedProject.enrollmentCurrencyId,
        currencyCode: selectedProject.enrollmentCurrencyCode || 'COP',
        currencySymbol: selectedProject.enrollmentCurrencySymbol || '$',
        totalAmount: 0,
        exchangeRate: selectedProject.enrollmentExchangeRate || 1
      };

      // Si no es COP, obtener tasa de cambio actual
      if (this.selectedEnrollmentInfo.currencyCode !== 'COP') {
        this.currentExchangeRate = this.selectedEnrollmentInfo.exchangeRate;
        this.getCurrentExchangeRate();
      } else {
        this.currentExchangeRate = 1;
      }
    }

    console.log('🤝 Cargando payment plans de participante corporativo:', {
      corporateEnrollmentId,
      participantId
    });
    this._loadingService.show();

    this._paymentPlansService.getPendingParticipantPaymentPlans(corporateEnrollmentId, participantId).subscribe({
      next: (response: any) => {
        console.log('✅ Payment plans de participante corporativo cargados:', response.data);
        // 🆕 Mapear planes inmediatamente, usar currentExchangeRate (se actualizará cuando llegue la respuesta de getCurrentExchangeRate)
        this.mapPaymentPlansToDropdown(response.data, 'participant-corporate');
        this._loadingService.hide();
      },
      error: (error: any) => {
        console.error('❌ Error cargando payment plans de participante corporativo:', error);
        this.toastService.showToast('error', 'Error cargando cuotas de pago del participante');
        this._loadingService.hide();
      }
    });
  }

  /**
   * 🆕 Mapea los payment plans del backend al formato del dropdown
   */
  private mapPaymentPlansToDropdown(paymentPlans: any[], type: string): void {
    this.paymentsPlans = (paymentPlans || []).map((data: any) => {
      console.log(`📋 Mapeando payment plan (${type}):`, {
        id: data.id,
        installmentNumber: data.installmentNumber,
        amountToPay: data.amountToPay,
        paymentStatus: data.paymentStatus,
        payerType: data.payerType,
        currencyCode: data.currencyCode,
        currencyId: data.currencyId,
        exchangeRateUsed: data.exchangeRateUsed,
        isPunished: data.isPunished
      });

      // 🆕 Prioridad: información de moneda del plan > selectedEnrollmentInfo > COP por defecto
      const currencyCode = data.currencyCode || this.selectedEnrollmentInfo?.currencyCode || 'COP';
      const currencySymbol = data.currencySymbol || this.selectedEnrollmentInfo?.currencySymbol || '$';
      // 🆕 SIEMPRE usar currentExchangeRate (última cotización vigente) para cálculos, NO exchangeRateUsed del plan
      // exchangeRateUsed es solo para referencia histórica de cuotas pagadas
      const exchangeRate = this.currentExchangeRate || 1;
      const isInternational = currencyCode !== 'COP';

      // 🆕 Actualizar selectedEnrollmentInfo con información del primer plan si no está establecido
      if (!this.selectedEnrollmentInfo && paymentPlans.length > 0 && data.currencyCode) {
        this.selectedEnrollmentInfo = {
          currencyId: data.currencyId,
          currencyCode: data.currencyCode,
          currencySymbol: data.currencySymbol,
          exchangeRate: data.exchangeRateUsed || 1 // Solo como fallback temporal
        };
        // 🆕 NO usar exchangeRateUsed del plan, obtener la última cotización vigente
        if (data.currencyCode !== 'COP') {
          console.log('💱 Obteniendo última cotización vigente para:', data.currencyCode);
          this.getCurrentExchangeRate();
        } else {
          this.currentExchangeRate = 1;
        }
        console.log('🔄 selectedEnrollmentInfo establecido desde plan:', this.selectedEnrollmentInfo);
      }

      const amountPaid = data.amountPaid || 0;
      const pendingPayment = data.pendingPayment || (data.amountToPay - amountPaid);

      // Formatear montos en la moneda de la inscripción
      const formatAmount = (amount: number) => {
        if (currencyCode === 'COP') {
          return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
        } else if (currencyCode === 'USD') {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
        } else {
          return `${currencySymbol}${new Intl.NumberFormat('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} (${currencyCode})`;
        }
      };

      // Calcular equivalente en COP si es internacional
      const getCOPEquivalent = (amount: number) => {
        if (!isInternational || !exchangeRate || exchangeRate === 1) return '';
        const copAmount = amount * exchangeRate;
        return ` ≈ ${new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(copAmount)}`;
      };

      const amountToPayFormatted = formatAmount(data.amountToPay) + getCOPEquivalent(data.amountToPay);
      const amountPaidFormatted = formatAmount(amountPaid) + getCOPEquivalent(amountPaid);
      const pendingPaymentFormatted = formatAmount(pendingPayment) + getCOPEquivalent(pendingPayment);

      const labelPrefix = data.isPunished ? '(CASTIGADA) ' : '';

      return {
        label: `${labelPrefix}Cuota: ${data.installmentNumber} | Monto a Pagar: ${amountToPayFormatted} |
                 Monto Pagado: ${amountPaidFormatted} |
                 Monto Pendiente: ${pendingPaymentFormatted} |
                     Vencimiento: ${new Date(data.dueDate).toLocaleDateString('es-CO')} | Estado: ${data.paymentStatus}`,
        value: data.id,
        data,
        disabled: data.isPunished || false
      };
    });

    console.log(`🔍 Payment plans procesados (${type}):`, this.paymentsPlans);
  }

  get incomeReceiptItems(): FormArray {
    return this.incomeReceiptForm.get('incomeReceiptItems') as FormArray;
  }

  atLeastOneValidItem(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const isValid = formArray.controls.some(item => item.valid);

      return isValid ? null : { atLeastOneItemRequired: true };
    };
  }

  // 🗑️ MÉTODO ELIMINADO: handleGetEnrollmentsByStudentId - Reemplazado por onParticipantChange

  handleGetPaymentPlansByProject(data: any) {
    const { value } = data
    console.log("mostrando data", data)

    // Reiniciar la categoría de ingreso al cambiar de proyecto
    this.incomeReceiptForm.patchValue({ projectBudgetId: null });
    this.incomeReceiptForm.get('projectBudgetId')?.markAsPristine();
    this.incomeReceiptForm.get('projectBudgetId')?.markAsUntouched();

    // 🔄 CORRECCIÓN: Obtener información del proyecto seleccionado desde el array projects
    console.log('🔍 === INICIANDO BÚSQUEDA DE INFORMACIÓN DEL PROYECTO ===');
    console.log('🔍 ProjectId seleccionado:', value);
    console.log('🔍 Proyectos disponibles:', this.projects);
    console.log('🔍 enrollmentsByStudent disponibles:', this.enrollmentsByStudent);

    // ✅ FIX: Buscar primero en el array projects que ya tiene el currencyCode mapeado
    const selectedProject = this.projects.find((x: any) => x.value === value);
    console.log('🔍 Proyecto encontrado en array projects:', selectedProject);

    if (selectedProject) {
      // Buscar la inscripción original para datos adicionales
      const selectedEnrollment = this.enrollmentsByStudent.find((x: any) => x.id === selectedProject?.enrollmentId || x.id === selectedProject?.corporateEnrollmentId);
      console.log('🔍 Inscripción original encontrada:', selectedEnrollment);

      // ✅ CORRECCIÓN: Usar currencyCode de la INSCRIPCIÓN, no del proyecto
      this.selectedEnrollmentInfo = {
        currencyId: selectedProject.enrollmentCurrencyId || selectedEnrollment?.currencyId,
        currencyCode: selectedProject.enrollmentCurrencyCode || 'COP', // ✅ Usar de la inscripción
        currencySymbol: selectedProject.enrollmentCurrencySymbol || '$',
        totalAmount: selectedEnrollment?.totalAmount,
        exchangeRate: selectedProject.enrollmentExchangeRate || 1
      };

      console.log('✅ === INFORMACIÓN DE INSCRIPCIÓN CONSTRUIDA ===');
      console.log('✅ selectedEnrollmentInfo:', this.selectedEnrollmentInfo);
      console.log('✅ currencyId:', this.selectedEnrollmentInfo.currencyId);
      console.log('✅ currencyCode (INSCRIPCIÓN):', this.selectedEnrollmentInfo.currencyCode);
      console.log('✅ currencySymbol:', this.selectedEnrollmentInfo.currencySymbol);
      console.log('✅ exchangeRate (INSCRIPCIÓN):', this.selectedEnrollmentInfo.exchangeRate);

      // ✅ Los métodos de pago son agnósticos a la moneda, no se filtran
      console.log('✅ Métodos de pago disponibles:', this.filteredPaymentMethods.length);

      // Si no es COP, obtener tasa de cambio actual
      if (this.selectedEnrollmentInfo.currencyCode !== 'COP') {
        console.log('💱 Moneda internacional detectada, obteniendo tasa de cambio...');
        // Usar la tasa de la inscripción como inicial
        this.currentExchangeRate = this.selectedEnrollmentInfo.exchangeRate;
        // Intentar obtener la tasa más actual
        this.getCurrentExchangeRate();
      } else {
        console.log('💵 Moneda nacional (COP) detectada, usando tasa 1');
        this.currentExchangeRate = 1;
      }
    } else {
      console.error('❌ === ERROR: NO SE ENCONTRÓ PROYECTO ===');
      console.error('❌ ProjectId buscado:', value);
      console.error('❌ Proyectos disponibles:', this.projects);
      console.warn('⚠️ No se encontró información del proyecto:', value);
      console.warn('⚠️ Disponibles:', this.enrollmentsByStudent.map((x: any) => ({ projectId: x.project?.id, projectName: x.project?.name })));

      // Asignar valores por defecto
      this.selectedEnrollmentInfo = {
        currencyId: null,
        currencyCode: 'COP',
        currencySymbol: '$',
        totalAmount: 0,
        exchangeRate: 1
      };
      this.currentExchangeRate = 1;
      console.warn('⚠️ Usando valores por defecto (COP)');
    }

    console.log('🚀 Iniciando carga de payment plans y categorías...');
    this.getAllPaymentPlansByProject(value);

    // loadIncomeProjectBudgets takes the actual project database ID, which we get from mapped selectedProject.projectId
    const actualProjectId = selectedProject ? selectedProject.projectId : value;
    this.loadIncomeProjectBudgets(actualProjectId);
  }

  loadIncomeProjectBudgets(projectId: number) {
    console.log('📊 Cargando categorías de ingreso para proyecto:', projectId);
    console.log('📊 URL será:', `ProjectBudget/project/${projectId}/income`);
    this._loadingService.show();

    this._projectService.getIncomeProjectBudgetsByProjectId(projectId).subscribe({
      next: (response: any) => {
        console.log('📊 Response completo de categorías:', response);
        console.log('📊 Datos de categorías:', response.data);
        console.log('📊 Tipo de response.data:', typeof response.data);
        console.log('📊 Es array?:', Array.isArray(response.data));

        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const budgetsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);

        if (budgetsData && budgetsData.length > 0) {
          this.incomeProjectBudgets = budgetsData.map((budget: any) => ({
            label: `${budget.budgetSubcategory.budgetCategory.name} - ${budget.budgetSubcategory.name}`,
            value: budget.id,
            data: budget
          }));
          console.log('✅ Categorías mapeadas exitosamente:', this.incomeProjectBudgets.length, 'elementos');
          console.log('✅ Primera categoría mapeada:', this.incomeProjectBudgets[0]);
          this.toastService.showToast('success', `${this.incomeProjectBudgets.length} categorías cargadas`);
        } else {
          this.incomeProjectBudgets = [];
          console.warn('⚠️ No se encontraron categorías de ingreso para el proyecto:', projectId);
          console.warn('⚠️ Response data vacío o null:', response.data);
          this.toastService.showToast('warning', 'No hay categorías de ingreso disponibles para este proyecto');
        }
        this._loadingService.hide();
      },
      error: (err) => {
        console.error('❌ Error cargando categorías de ingreso:', err);
        console.error('❌ Error status:', err.status);
        console.error('❌ Error message:', err.message);
        console.error('❌ Error completo:', err);
        this.incomeProjectBudgets = [];
        this.toastService.showToast('error', 'Error cargando categorías de ingreso: ' + (err.message || 'Error desconocido'));
        this._loadingService.hide();
      }
    });
  }

  handleGetGrossAmountPaymentPlan(data: any) {
    const { value } = data;
    console.log(value)
    const paymentPlanData = this.paymentsPlans.find((x: any) => x.data.id == value);
    console.log("mostrando payment plan data", paymentPlanData)

    // 🆕 Actualizar selectedEnrollmentInfo con información de moneda del plan si está disponible
    if (paymentPlanData?.data) {
      const planData = paymentPlanData.data;

      // Si el plan tiene información de moneda, actualizar selectedEnrollmentInfo
      if (planData.currencyCode || planData.currencyId) {
        this.selectedEnrollmentInfo = {
          ...this.selectedEnrollmentInfo,
          currencyId: planData.currencyId || this.selectedEnrollmentInfo?.currencyId,
          currencyCode: planData.currencyCode || this.selectedEnrollmentInfo?.currencyCode || 'COP',
          currencySymbol: planData.currencySymbol || this.selectedEnrollmentInfo?.currencySymbol || '$',
          // 🆕 NO usar exchangeRateUsed del plan, solo como fallback temporal
          exchangeRate: this.selectedEnrollmentInfo?.exchangeRate || planData.exchangeRateUsed || 1
        };

        console.log('🔄 selectedEnrollmentInfo actualizado desde plan:', this.selectedEnrollmentInfo);

        // 🆕 SIEMPRE obtener la última cotización vigente, no usar la histórica del plan
        if (this.selectedEnrollmentInfo.currencyCode !== 'COP') {
          console.log('💱 Obteniendo última cotización vigente para:', this.selectedEnrollmentInfo.currencyCode);
          this.getCurrentExchangeRate();
        } else {
          this.currentExchangeRate = 1;
        }
      } else {
        // Si no hay información de moneda en el plan, intentar obtener tasa actual si ya tenemos selectedEnrollmentInfo
        if (this.selectedEnrollmentInfo?.currencyCode && this.selectedEnrollmentInfo.currencyCode !== 'COP') {
          this.getCurrentExchangeRate();
        }
      }
    }

    // Establecer el monto pendiente como valor a pagar
    this.incomeReceiptForm.get('grossAmount')?.setValue(paymentPlanData.data.pendingPayment);

    // 🆕 Seleccionar automáticamente "Cuota Cartera" cuando se selecciona una cuota
    const conceptoControl = this.incomeReceiptForm.get('concept');
    if (conceptoControl) {
      conceptoControl.setValue('Cuota Cartera');
      console.log('✅ Concepto "Cuota Cartera" seleccionado automáticamente');
    }

    // Calcular equivalente en COP automáticamente (usará la tasa actualizada)
    this.calculateCOPEquivalent();

    // Cargar allocations (empresa/estudiante)
    this._paymentPlansService.getAllocations(paymentPlanData.data.id).subscribe({
      next: (allocs) => {
        this.planAllocations = allocs || [];
        // Si hay split, forzar selección explícita; si no, inferir
        if (this.planAllocations.length > 1) {
          this.payerSelection = null;
        } else if (this.planAllocations.length === 1) {
          this.payerSelection = this.planAllocations[0].allocationType;
          this.incomeReceiptForm.get('grossAmount')?.setValue(this.planAllocations[0].amount);
          this.calculateCOPEquivalent();
        }
      },
      error: (err) => {
        console.error('Error obteniendo allocations', err);
        this.planAllocations = [];
      }
    });
  }

  onChangePayerSelection(type: 'COMPANY' | 'PARTICIPANT') {
    this.payerSelection = type;
    const alloc = this.planAllocations.find((a: any) => a.allocationType === type);
    if (alloc) {
      this.incomeReceiptForm.get('grossAmount')?.setValue(alloc.amount);
      this.calculateCOPEquivalent();
    }
  }


  async onSubmit(): Promise<void> {
    await this.handleFormSubmit();
  }

  private async submitForm(): Promise<void> {
    this._loadingService.show();
    console.log("before", this.incomeReceiptForm.value)

    // 🆕 Construir el objeto con la información del pagador según el tipo
    const payerType = this.incomeReceiptForm.value.payerType;
    const participantId = this.incomeReceiptForm.value.participantId;
    const companyId = this.incomeReceiptForm.value.companyId;

    console.log('🔍 Construyendo payload - PayerType:', payerType, 'ParticipantId:', participantId, 'CompanyId:', companyId);

    // 🎯 DEBUG: Mostrar cómo se procesan los payment plan IDs
    const formPaymentPlanId = this.incomeReceiptForm.value.paymentPlanId;
    const finalPaymentPlanId = this.getCorrectPaymentPlanId(payerType, formPaymentPlanId);
    const finalCorporatePaymentPlanId = this.getCorrectCorporatePaymentPlanId(payerType, formPaymentPlanId);

    console.log('📋 Payment Plan IDs Debug:', {
      formPaymentPlanId,
      payerType,
      finalPaymentPlanId,
      finalCorporatePaymentPlanId,
      isIndividual: payerType === 'PARTICIPANT',
      isCorporate: payerType === 'COMPANY' || payerType === 'PARTICIPANT_CORPORATE'
    });

    const selectedProject = this.projects.find((p: any) => p.value === this.incomeReceiptForm.value.projectId);
    const actualProjectId = selectedProject ? selectedProject.projectId : Number(this.incomeReceiptForm.value.projectId);

    // 🎯 CONSTRUIR PAYLOAD DINÁMICO - Solo incluir campos necesarios según el tipo de pagador
    const basePayload = {
      projectId: actualProjectId,
      projectBudgetId: this.incomeReceiptForm.value.projectBudgetId !== null && this.incomeReceiptForm.value.projectBudgetId !== undefined
        ? Number(this.incomeReceiptForm.value.projectBudgetId)
        : null,
      paymentMethodId: Number(this.incomeReceiptForm.value.paymentMethodId),
      bankId: this.incomeReceiptForm.value.bankId && this.incomeReceiptForm.value.bankId !== ''
        ? Number(this.incomeReceiptForm.value.bankId)
        : null,
      grossAmount: parseFloat(this.incomeReceiptForm.value.grossAmount),
      currencyId: this.selectedEnrollmentInfo?.currencyId || null,
      isManualPayment: this.incomeReceiptForm.value.paymentMethodId != 3 ? true : false,
      referenceNumber: this.incomeReceiptForm.value.referenceNumber || "",
      concept: this.incomeReceiptForm.value.concept,
      observations: this.incomeReceiptForm.value.observations || "",
      transactionDate: this.incomeReceiptForm.value.transactionDate, // 🆕 Agregar fecha de transacción
      feeDetails: (this.incomeReceiptForm.value.feeDetails || []).map((f: any) => ({
        ...f,
        requestAmount: f.requestAmount?.toString() || ''
      })),

      // 🆕 LÓGICA CORREGIDA SEGÚN TIPO DE PAGADOR
      payerType: payerType,

      // 🎯 PARTICIPANTID SEGÚN TIPO DE PAGADOR
      participantId: this.getCorrectParticipantId(payerType, participantId),

      // 🎯 COMPANYID SEGÚN TIPO DE PAGADOR
      companyId: this.getCorrectCompanyId(payerType, companyId)
    };

    // 🎯 AGREGAR PAYMENT PLAN ID DINÁMICAMENTE - Solo el que corresponde
    const newIncomeReceipt: any = { ...basePayload };

    if (finalPaymentPlanId !== null) {
      // Solo para pagos individuales (PARTICIPANT)
      newIncomeReceipt.paymentPlanId = finalPaymentPlanId;
      console.log('✅ Agregado PaymentPlanId:', finalPaymentPlanId);
    }

    if (finalCorporatePaymentPlanId !== null) {
      // Solo para pagos corporativos (COMPANY, PARTICIPANT_CORPORATE)
      newIncomeReceipt.corporatePaymentPlanId = finalCorporatePaymentPlanId;
      console.log('✅ Agregado CorporatePaymentPlanId:', finalCorporatePaymentPlanId);
    }

    console.log('📤 Payload final:', newIncomeReceipt);

    // 🆕 MODO EDICIÓN: usar createEditedVersion en lugar de insert
    if (this.isEditMode && this.editFromId) {
      console.log('✏️ Creando versión editada del comprobante:', this.editFromId);
      const editPayload = {
        ...newIncomeReceipt,
        editJustification: this.editJustification || 'Edición sin justificación específica'
      };

      this._incomeReceiptService.createEditedVersion(this.editFromId, editPayload).subscribe({
        next: (response: any) => {
          this._loadingService.hide();
          console.log('📋 Response edición:', response);
          this.handleSuccess('Versión editada creada exitosamente. Pendiente de aprobación.');
        },
        error: (err: any) => {
          this._loadingService.hide();
          console.error('❌ Error creando versión editada:', err);
          this.handleError(`Error al crear la versión editada: ${err}`);
        }
      });
      return;
    }

    // 🆕 Usar método dinámico en lugar de hardcode
    if (this.shouldGeneratePaymentLink()) {
      console.log('🔗 Generando link de pago...');
      this._incomeReceiptService.creteLinkPayment(newIncomeReceipt).subscribe({
        next: (response: any) => {
          this._loadingService.hide();
          console.log("📋 Response link de pago:", response);

          // 🎉 Procesar respuesta exitosa del link
          this.handleLinkPaymentSuccess(response);
        },
        error: (err) => {
          this._loadingService.hide();
          console.error("❌ Error creando link de pago:", err);
          this.handleError(`Error al crear el link de pago: ${err}`);
        }
      })
    } else {
      console.log('💰 Creando comprobante de ingreso...');
      this._incomeReceiptService.insert(newIncomeReceipt).subscribe({
        next: (response: any) => {
          this._loadingService.hide();
          console.log("📋 Response comprobante:", response);

          // Si el backend retorna un resumen de comprobantes generados, mostrar pantalla de éxito
          if (response?.creationResult && response.creationResult.totalReceiptsGenerated > 0) {
            this.handleCreationResultSuccess(response);
          } else {
            this.handleSuccess('Comprobante de ingreso creado exitosamente');
          }
        },
        error: (err) => {
          this._loadingService.hide();
          console.error("❌ Error creando comprobante:", err);
          this.handleError(`Error al crear el comprobante de ingreso: ${err}`);
        }
      })
    }

    console.log("after", newIncomeReceipt)
  }

  /**
   * 🎉 Maneja la respuesta exitosa de creación de link de pago
   */
  private handleLinkPaymentSuccess(response: any): void {
    console.log('🎉 Procesando éxito de link de pago:', response);

    // Guardar información del link generado
    this.generatedPaymentLink = response.paymentLink;
    this.isLinkGenerated = true;
    this.showSuccessStep = true;

    // Cambiar a 4 pasos para incluir la pantalla de éxito
    this.totalSteps = 4;

    // Navegar automáticamente al paso de éxito (paso 4)
    this.goToStep(4);

    // Mostrar toast de éxito
    this.toastService.showToast('success', response.message || 'Link de pago generado exitosamente');

    console.log('✅ Link guardado:', this.generatedPaymentLink);
  }

  private handleCreationResultSuccess(response: any): void {
    this.creationResult = response.creationResult;
    this.showCreationResult = true;
    this.totalSteps = 4;
    this.goToStep(4);
    this.toastService.showToast('success', `Se generaron ${this.creationResult.totalReceiptsGenerated} comprobante(s) de ingreso exitosamente`);
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/income-receipts']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  /**
   * ✅ ELIMINADO: Los métodos de pago son agnósticos a la moneda
   * Se cargan todos los métodos sin filtrar en loadInitialData()
   */

  /**
   * Obtiene la tasa de cambio actual para la moneda de la inscripción
   */
  getCurrentExchangeRate(): void {
    if (!this.selectedEnrollmentInfo || this.selectedEnrollmentInfo.currencyCode === 'COP') {
      this.currentExchangeRate = 1;
      console.log('Moneda COP detectada, usando tasa 1');
      return;
    }

    console.log(`Obteniendo tasa de cambio actual para ${this.selectedEnrollmentInfo.currencyCode} -> COP`);
    console.log(`Tasa de inscripción como fallback: ${this.selectedEnrollmentInfo.exchangeRate}`);

    this._exchangeRateService.getLatestRate(this.selectedEnrollmentInfo.currencyCode, 'COP')
      .subscribe({
        next: (response: any) => {
          console.log('Respuesta del servicio de tasa de cambio:', response);
          if (response.isSuccess && response.rate > 0) {
            this.currentExchangeRate = response.rate;
            console.log(`✅ Tasa de cambio actualizada desde API: ${this.currentExchangeRate}`);
          } else {
            // Usar la tasa de cambio de la inscripción como fallback
            this.currentExchangeRate = this.selectedEnrollmentInfo.exchangeRate;
            console.log(`⚠️ Usando tasa de la inscripción como fallback: ${this.currentExchangeRate}`);
          }
          // Recalcular equivalente después de obtener la tasa
          this.calculateCOPEquivalent();
          // 🆕 Si hay planes ya mapeados, remapearlos con la nueva tasa
          if (this.paymentsPlans && this.paymentsPlans.length > 0) {
            console.log('🔄 Remapeando payment plans con nueva tasa de cambio...');
            const plansData = this.paymentsPlans.map((p: any) => p.data);
            const lastType = this.paymentsPlans[0]?.data?.payerType === 'COMPANY' ? 'company' :
              this.paymentsPlans[0]?.data?.payerType === 'PARTICIPANT' ? 'participant-corporate' : 'individual';
            this.mapPaymentPlansToDropdown(plansData, lastType);
          }
        },
        error: (err) => {
          console.error('Error obteniendo tasa de cambio:', err);
          // Usar la tasa de cambio de la inscripción como fallback
          this.currentExchangeRate = this.selectedEnrollmentInfo.exchangeRate;
          console.log(`⚠️ Error en API, usando tasa de la inscripción: ${this.currentExchangeRate}`);
          // Recalcular equivalente después del error
          this.calculateCOPEquivalent();
          // 🆕 Si hay planes ya mapeados, remapearlos con la tasa de fallback
          if (this.paymentsPlans && this.paymentsPlans.length > 0) {
            console.log('🔄 Remapeando payment plans con tasa de fallback...');
            const plansData = this.paymentsPlans.map((p: any) => p.data);
            const lastType = this.paymentsPlans[0]?.data?.payerType === 'COMPANY' ? 'company' :
              this.paymentsPlans[0]?.data?.payerType === 'PARTICIPANT' ? 'participant-corporate' : 'individual';
            this.mapPaymentPlansToDropdown(plansData, lastType);
          }
        }
      });
  }

  /**
   * Calcula el equivalente en COP del monto ingresado
   */
  calculateCOPEquivalent(): void {
    const grossAmount = parseFloat(this.incomeReceiptForm.get('grossAmount')?.value || '0');

    console.log(`Calculando equivalente COP para monto: ${grossAmount}`);
    console.log(`Moneda seleccionada: ${this.selectedEnrollmentInfo?.currencyCode}`);
    console.log(`Tasa de cambio actual: ${this.currentExchangeRate}`);

    if (!this.selectedEnrollmentInfo || this.selectedEnrollmentInfo.currencyCode === 'COP') {
      this.copEquivalent = 0;
      console.log('Moneda COP o no hay información, equivalente = 0');
      return;
    }

    if (grossAmount > 0 && this.currentExchangeRate > 0) {
      this.copEquivalent = grossAmount * this.currentExchangeRate;
      console.log(`Equivalente calculado: ${this.copEquivalent} COP`);
    } else {
      this.copEquivalent = 0;
      console.log('Monto o tasa inválidos, equivalente = 0');
    }
  }

  /**
   * Formatea un monto según la moneda especificada
   */
  formatCurrency(amount: number, currencyCode: string): string {
    if (currencyCode === 'COP') {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } else if (currencyCode === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } else {
      return `${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode}`;
    }
  }

  /**
   * Formatea el monto de un plan de pago basado en la moneda de la inscripción
   */
  formatCurrencyFromPlan(amount: number, planData: any): string {
    // Usar la moneda de la inscripción si está disponible
    const currencyCode = this.selectedEnrollmentInfo?.currencyCode || 'COP';
    const formatted = this.formatCurrency(amount, currencyCode);

    // Si es internacional, agregar equivalente en COP
    if (currencyCode !== 'COP' && this.currentExchangeRate && this.currentExchangeRate !== 1 && amount > 0) {
      const copEquivalent = amount * this.currentExchangeRate;
      return `${formatted} ≈ ${this.formatCurrency(copEquivalent, 'COP')}`;
    }

    return formatted;
  }

  /**
   * Obtiene el equivalente en COP de un monto en moneda internacional
   */
  getCOPEquivalentForAmount(amount: number): string {
    if (!this.selectedEnrollmentInfo || this.selectedEnrollmentInfo.currencyCode === 'COP') {
      return '';
    }

    if (this.currentExchangeRate && this.currentExchangeRate !== 1 && amount > 0) {
      const copAmount = amount * this.currentExchangeRate;
      return this.formatCurrency(copAmount, 'COP');
    }

    return '';
  }

  // 🆕 ===== MÉTODOS DEL WIZARD (Principio SRP) =====

  /**
   * Navegar a un paso específico del wizard
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // 🆕 Si intentamos avanzar al step 2, recargar proyectos primero
      if (step === 2 && this.step === 1) {
        this.reloadProjectsBeforeStep2().then(() => {
          // Una vez cargados los proyectos, avanzar al step 2
          this.step = step;
          // Limpiar el valor del proyecto hasta que el usuario lo seleccione
          this.incomeReceiptForm.patchValue({ projectId: null });
        }).catch((error) => {
          console.error('Error recargando proyectos:', error);
          this.toastService.showToast('error', 'Error al cargar proyectos. Por favor, intente nuevamente.');
        });
      } else {
        this.step = step;
      }
    }
  }

  /**
   * 🆕 Recargar proyectos antes de avanzar al step 2
   */
  private reloadProjectsBeforeStep2(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('🔄 Recargando proyectos antes de avanzar al step 2...');

      // Limpiar el array de proyectos
      this.projects = [];

      // Limpiar el valor del proyecto en el formulario
      this.incomeReceiptForm.patchValue({ projectId: null });

      // Obtener el tipo de pagador y datos necesarios
      const payerType = this.incomeReceiptForm.get('payerType')?.value;
      const participantId = this.incomeReceiptForm.get('participantId')?.value;
      const companyId = this.incomeReceiptForm.get('companyId')?.value;

      console.log('🔄 Datos para recargar:', { payerType, participantId, companyId });

      // Cargar proyectos según el tipo de pagador
      let loadPromise: Promise<void>;

      switch (payerType) {
        case 'PARTICIPANT':
          if (participantId && participantId !== 0) {
            loadPromise = this.loadIndividualEnrollments(participantId);
          } else {
            reject(new Error('No hay participante seleccionado'));
            return;
          }
          break;

        case 'COMPANY':
          if (companyId && companyId !== 0) {
            loadPromise = this.loadCorporateEnrollmentsByCompany(companyId);
          } else {
            reject(new Error('No hay empresa seleccionada'));
            return;
          }
          break;

        case 'PARTICIPANT_CORPORATE':
          if (participantId && participantId !== 0) {
            loadPromise = this.loadCorporateEnrollmentsByParticipant(participantId);
          } else {
            reject(new Error('No hay participante seleccionado'));
            return;
          }
          break;

        default:
          reject(new Error('Tipo de pagador no válido'));
          return;
      }

      // Esperar a que se carguen los proyectos
      loadPromise.then(() => {
        console.log('✅ Proyectos recargados exitosamente:', this.projects.length, 'proyectos');
        // Forzar detección de cambios para actualizar el dropdown
        this.cdRef.detectChanges();
        resolve();
      }).catch((error) => {
        console.error('❌ Error recargando proyectos:', error);
        reject(error);
      });
    });
  }

  /**
   * Validar si el paso actual es válido
   */
  isCurrentStepValid(): boolean {
    switch (this.step) {
      case 1:
        return this.isStep1Valid();
      case 2:
        return this.isStep2Valid();
      case 3:
        return this.isStep3Valid();
      case 4:
        return this.isStep4Valid(); // Paso de éxito siempre es válido
      default:
        return false;
    }
  }

  /**
   * Validar Step 1: Información del Pagador
   */
  private isStep1Valid(): boolean {
    const payerType = this.incomeReceiptForm.get('payerType')?.value;
    const participantId = this.incomeReceiptForm.get('participantId')?.value;
    const companyId = this.incomeReceiptForm.get('companyId')?.value;

    // Validación básica de participante
    const hasParticipant = participantId && participantId > 0;

    let isValid = false;

    // Validación específica por tipo de pagador
    switch (payerType) {
      case 'PARTICIPANT':
        // Para pagos individuales: requiere participante Y que tenga inscripciones
        isValid = hasParticipant && this.participantHasEnrollments === true;
        break;
      case 'COMPANY':
        // Para pagos de empresa: requiere empresa Y que tenga inscripciones corporativas
        isValid = companyId && companyId > 0 && this.companyHasEnrollments === true;
        break;
      case 'PARTICIPANT_CORPORATE':
        // Para pagos corporativos compartidos: requiere participante con inscripciones corporativas Y empresa seleccionada
        // (las empresas ya están pre-filtradas para tener inscripciones corporativas con el participante)
        isValid = hasParticipant && this.participantHasEnrollments === true && companyId && companyId > 0;
        break;
      default:
        isValid = false;
    }

    console.log('🔍 Validación Step 1:', {
      payerType,
      participantId,
      companyId,
      hasParticipant,
      participantHasEnrollments: this.participantHasEnrollments,
      companyHasEnrollments: this.companyHasEnrollments,
      isValid
    });

    return isValid;
  }

  /**
   * Validar Step 2: Detalles del Pago
   */
  private isStep2Valid(): boolean {
    return !!(
      this.incomeReceiptForm.get('projectId')?.valid &&
      this.incomeReceiptForm.get('concept')?.valid &&
      this.incomeReceiptForm.get('paymentMethodId')?.valid &&
      this.incomeReceiptForm.get('bankId')?.valid &&
      this.incomeReceiptForm.get('grossAmount')?.valid &&
      this.incomeReceiptForm.get('projectBudgetId')?.valid &&
      this.incomeReceiptForm.get('paymentPlanId')?.valid
    );
  }

  /**
   * Validar Step 3: Confirmación y Resumen
   */
  private isStep3Valid(): boolean {
    return this.incomeReceiptForm.valid;
  }

  /**
   * Validar Step 4: Pantalla de Éxito del Link
   */
  private isStep4Valid(): boolean {
    // El paso 4 siempre es válido ya que solo muestra información
    return this.isLinkGenerated && this.generatedPaymentLink != null;
  }

  /**
   * Manejar cambio de tipo de pagador
   */
  async onPayerTypeChange(payerType: 'PARTICIPANT' | 'COMPANY' | 'PARTICIPANT_CORPORATE'): Promise<void> {
    console.log('🔄 Cambio de tipo de pagador:', payerType);

    // Confirmar cambio si hay datos que se perderán y el formulario ya tiene valores
    if (this.selectedPayerType && this.selectedPayerType !== payerType && this.hasUnsavedChanges()) {
      const fromType = this.getPayerTypeDisplayName();
      const toType = this.getPayerTypeDisplayName(payerType);

      const canChange = await this.confirmationService.incomeReceipt.changePayerType(fromType, toType);
      if (!canChange) {
        // Revertir el cambio en la UI si es necesario
        return;
      }
    }

    this.participantHasEnrollments = null;
    this.participantEnrollmentCheckMessage = '';
    this.companyHasEnrollments = null;
    this.companyEnrollmentCheckMessage = '';

    this.selectedPayerType = payerType;
    this.incomeReceiptForm.patchValue({ payerType });

    // Limpiar datos dependientes
    this.projects = [];
    this.enrollmentsByStudent = [];
    this.paymentsPlans = [];
    this.selectedEnrollmentInfo = null;

    // Resetear formulario
    this.incomeReceiptForm.patchValue({
      participantId: 0,
      projectId: 0,
      paymentPlanId: 0
    });

    // 🆕 CORRECCIÓN: Limpiar empresas según el tipo de pagador
    if (payerType === 'PARTICIPANT') {
      // Pago individual: limpiar empresa
      this.selectedCompanyId = null;
      this.companies = [];
      this.incomeReceiptForm.patchValue({ companyId: null });
    } else if (payerType === 'PARTICIPANT_CORPORATE') {
      // Participante de empresa: limpiar empresas y esperar selección de participante
      this.selectedCompanyId = null;
      this.companies = [];
      this.incomeReceiptForm.patchValue({ companyId: null });
      console.log('🤝 Modo PARTICIPANT_CORPORATE: empresas se cargarán al seleccionar participante');
    }
    // Para COMPANY, las empresas ya están cargadas en loadInitialData

    // Actualizar validadores según el tipo
    this.updateValidators();
  }

  /**
   * Actualizar validadores dinámicamente según el tipo de pagador (Principio OCP)
   */
  private updateValidators(): void {
    const companyIdControl = this.incomeReceiptForm.get('companyId');
    const participantIdControl = this.incomeReceiptForm.get('participantId');

    if (this.selectedPayerType === 'COMPANY' || this.selectedPayerType === 'PARTICIPANT_CORPORATE') {
      // Requerir empresa para pagos empresariales o de participante corporativo
      companyIdControl?.setValidators([Validators.required]);
    } else {
      // No requerir empresa para pagos de participantes individuales
      companyIdControl?.clearValidators();
    }

    if (this.selectedPayerType === 'COMPANY') {
      // Para empresa no requerir participante
      participantIdControl?.clearValidators();
      this.incomeReceiptForm.patchValue({ participantId: null });
    } else {
      // Para otros tipos sí requerir participante
      participantIdControl?.setValidators([Validators.required]);
    }

    companyIdControl?.updateValueAndValidity();
    participantIdControl?.updateValueAndValidity();

    // 🆕 paymentPlanId depende de selectedPayerType (vía su validador condicional)
    this.incomeReceiptForm.get('paymentPlanId')?.updateValueAndValidity();
  }

  /**
   * Obtener nombre descriptivo del tipo de pagador
   */
  getPayerTypeDisplayName(payerType?: string): string {
    const type = payerType || this.selectedPayerType;
    switch (type) {
      case 'COMPANY':
        return '🏢 Empresa';
      case 'PARTICIPANT':
        return '👤 Participante';
      case 'PARTICIPANT_CORPORATE':
        return '🤝 Compartido';
      default:
        return '';
    }
  }

  /**
   * Obtener descripción del tipo de pagador
   */
  getPayerTypeDescription(): string {
    switch (this.selectedPayerType) {
      case 'COMPANY':
        return 'El pago será registrado como realizado por la empresa';
      case 'PARTICIPANT':
        return 'El pago será registrado como realizado por el participante/estudiante';
      case 'PARTICIPANT_CORPORATE':
        return 'El pago es compartido entre la empresa y el participante';
      default:
        return '';
    }
  }

  /**
   * Obtener empresa seleccionada
   */
  getSelectedCompany(): any {
    if (!this.selectedCompanyId || !this.companies) return null;
    return this.companies.find(c => c.value === this.selectedCompanyId);
  }

  /**
   * Obtener participante seleccionado
   */
  getSelectedParticipant(): any {
    const participantId = this.incomeReceiptForm.get('participantId')?.value;
    if (!participantId || !this.participants) return null;
    return this.participants.find((p: any) => p.value === participantId);
  }

  /**
   * Obtener nombre del método de pago seleccionado
   */
  getSelectedPaymentMethodName(): string {
    const paymentMethodId = this.incomeReceiptForm.get('paymentMethodId')?.value;
    if (!paymentMethodId || !this.filteredPaymentMethods) return 'Sin método';
    const method = this.filteredPaymentMethods.find((pm: any) => pm.value === paymentMethodId);
    return method?.label || 'Sin método';
  }

  /**
   * Obtener valor del concepto seleccionado
   */
  getSelectedConceptLabel(): string {
    const conceptValue = this.incomeReceiptForm.get('concept')?.value;
    if (!conceptValue) return 'Sin concepto';
    return conceptValue.label || conceptValue || 'Sin concepto';
  }

  /**
   * 🆕 Obtener código de moneda con prioridad múltiple
   */
  getCurrencyCode(): string {
    // Prioridad 1: selectedEnrollmentInfo
    if (this.selectedEnrollmentInfo?.currencyCode) {
      return this.selectedEnrollmentInfo.currencyCode;
    }

    // Prioridad 2: proyecto seleccionado
    const selectedProjectId = this.incomeReceiptForm.get('projectId')?.value;
    if (selectedProjectId) {
      const selectedProject = this.projects.find((p: any) => p.value === selectedProjectId);
      if (selectedProject?.enrollmentCurrencyCode) {
        return selectedProject.enrollmentCurrencyCode;
      }
    }

    // Prioridad 3: plan de pago seleccionado
    const selectedPaymentPlanId = this.incomeReceiptForm.get('paymentPlanId')?.value;
    if (selectedPaymentPlanId) {
      const selectedPlan = this.paymentsPlans.find((p: any) => p.data.id === selectedPaymentPlanId);
      if (selectedPlan?.data?.currencyCode) {
        return selectedPlan.data.currencyCode;
      }
    }

    // Por defecto: COP
    return 'COP';
  }

  /**
   * Obtener monto bruto formateado
   */
  getFormattedGrossAmount(): string {
    const amount = this.incomeReceiptForm.get('grossAmount')?.value || 0;
    const currencyCode = this.getCurrencyCode();
    return this.formatCurrency(amount, currencyCode);
  }

  /**
   * Obtener código de moneda actual
   */
  getCurrentCurrencyCode(): string {
    return this.selectedEnrollmentInfo?.currencyCode || 'COP';
  }

  /**
   * Verificar si es moneda no COP
   */
  isNonCOPCurrency(): boolean {
    return this.selectedEnrollmentInfo?.currencyCode !== 'COP' && this.copEquivalent > 0;
  }

  /**
   * Obtener observaciones del formulario
   */
  getObservations(): string {
    return this.incomeReceiptForm.get('observations')?.value || '';
  }

  getSelectedProjectLabel(): string {
    const projectId = this.incomeReceiptForm.get('projectId')?.value;
    if (!projectId || !this.projects) return 'Sin proyecto';
    const project = this.projects.find((p: any) => p.value === projectId);
    return project?.label || 'Sin proyecto';
  }

  getSelectedBankLabel(): string {
    const bankId = this.incomeReceiptForm.get('bankId')?.value;
    if (!bankId || !this.banks) return 'Sin banco';
    const bank = this.banks.find((b: any) => b.value === bankId);
    return bank?.label || 'Sin banco';
  }

  getSelectedIncomeCategoryLabel(): string {
    const budgetId = this.incomeReceiptForm.get('projectBudgetId')?.value;
    if (!budgetId || !this.incomeProjectBudgets) return 'Sin categoría';
    const budget = this.incomeProjectBudgets.find((b: any) => b.value === budgetId);
    return budget?.label || 'Sin categoría';
  }

  getReferenceNumber(): string {
    return this.incomeReceiptForm.get('referenceNumber')?.value || '';
  }

  getSelectedPaymentPlanLabel(): string {
    const planId = this.incomeReceiptForm.get('paymentPlanId')?.value;
    if (!planId || !this.paymentsPlans) return 'Sin cuota';
    const plan = this.paymentsPlans.find((p: any) => p.value === planId);
    return plan?.label || 'Sin cuota';
  }

  getSelectedFeeTypeName(feeTypeId: any): string {
    if (!feeTypeId || !this.feeTypes) return 'Desconocido';
    const feeType = this.feeTypes.find((f: any) => f.id == feeTypeId);
    return feeType?.name || 'Desconocido';
  }

  /**
   * Verificar si el método de pago requiere generación de link
   * 🆕 Actualizado para usar lógica dinámica
   */
  isLinkPayment(): boolean {
    return this.shouldGeneratePaymentLink();
  }

  // 🆕 ===== MÉTODOS PARA MANEJO DEL LINK GENERADO =====

  /**
   * 📋 Copiar link de pago al portapapeles
   */
  async copyPaymentLink(): Promise<void> {
    if (!this.generatedPaymentLink?.paymentUrl) {
      this.toastService.showToast('error', 'No hay link para copiar');
      return;
    }

    try {
      await navigator.clipboard.writeText(this.generatedPaymentLink.paymentUrl);
      this.toastService.showToast('success', '¡Link copiado al portapapeles!');
    } catch (err) {
      console.error('Error copiando al portapapeles:', err);
      // Fallback para navegadores que no soportan clipboard API
      this.fallbackCopyTextToClipboard(this.generatedPaymentLink.paymentUrl);
    }
  }

  /**
   * Fallback para copiar texto en navegadores antiguos
   */
  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.toastService.showToast('success', '¡Link copiado al portapapeles!');
      } else {
        this.toastService.showToast('error', 'No se pudo copiar el link');
      }
    } catch (err) {
      console.error('Fallback: Error copiando texto:', err);
      this.toastService.showToast('error', 'No se pudo copiar el link');
    }

    document.body.removeChild(textArea);
  }

  /**
   * 📅 Obtener fecha de expiración formateada
   */
  getFormattedExpirationDate(): string {
    if (!this.generatedPaymentLink?.expirationDate) {
      return 'No especificada';
    }

    const expirationDate = new Date(this.generatedPaymentLink.expirationDate);
    return expirationDate.toLocaleString('es-CO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * 📊 Obtener monto formateado del link
   */
  getFormattedLinkAmount(): string {
    if (!this.generatedPaymentLink?.amount) {
      return '$0';
    }

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(this.generatedPaymentLink.amount);
  }

  /**
   * ⏰ Verificar si el link está próximo a vencer (menos de 24 horas)
   */
  isLinkExpiringSoon(): boolean {
    if (!this.generatedPaymentLink?.expirationDate) {
      return false;
    }

    const expirationDate = new Date(this.generatedPaymentLink.expirationDate);
    const now = new Date();
    const hoursUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    return hoursUntilExpiration <= 24 && hoursUntilExpiration > 0;
  }

  /**
   * 🔄 Ir a la lista de comprobantes
   */
  goToIncomeReceiptsList(): void {
    this.router.navigate(['/income-receipts']);
  }

  /**
   * 🆕 Crear otro link de pago (reiniciar formulario)
   */
  async createAnotherLink(): Promise<void> {
    const canReset = await this.confirmationService.confirmFormReset('el formulario de comprobante de ingreso');
    if (!canReset) {
      return;
    }
    // Resetear estado del link
    this.generatedPaymentLink = null;
    this.isLinkGenerated = false;
    this.showSuccessStep = false;
    this.totalSteps = 3;

    // Resetear formulario
    this.incomeReceiptForm.reset();
    this.incomeReceiptForm.patchValue({
      payerType: this.selectedPayerType,
      participantId: 0,
      projectId: 0,
      grossAmount: 0,
      currencyId: 1,
      paymentMethodId: 0,
      bankId: '',
      projectBudgetId: null
    });

    // Limpiar datos dependientes
    this.paymentsPlans = [];
    this.incomeProjectBudgets = [];
    this.selectedEnrollmentInfo = null;
    this.copEquivalent = 0;
    this.currentExchangeRate = 1;

    // Volver al paso 1
    this.goToStep(1);

    this.toastService.showToast('success', 'Formulario reiniciado. Puede crear un nuevo link de pago.');
  }

  /**
   * 🆕 Inicialización con parámetros URL sin setTimeout artificiales
   */
  private initializeWithUrlParams(projectId: number, installmentNumber?: string, payerType?: string): void {
    console.log('🚀 Inicializando con parámetros URL:', { projectId, installmentNumber, payerType });

    if (!projectId) {
      console.warn('⚠️ No se proporcionó projectId');
      return;
    }

    // Cargar inscripciones basado en el tipo de pagador
    switch (payerType) {
      case 'individual':
        const participantId = this.incomeReceiptForm.get('participantId')?.value;
        if (participantId) {
          this.loadIndividualEnrollments(participantId).then(() => {
            this.selectProjectAndInstallment(projectId, installmentNumber);
          });
        }
        break;

      case 'company':
        if (this.selectedCompanyId) {
          this.loadCorporateEnrollmentsByCompany(this.selectedCompanyId).then(() => {
            this.selectProjectAndInstallment(projectId, installmentNumber);
          });
        }
        break;

      case 'participant_corporate':
        const participantIdCorp = this.incomeReceiptForm.get('participantId')?.value;
        if (participantIdCorp) {
          this.loadCorporateEnrollmentsByParticipant(participantIdCorp).then(() => {
            this.selectProjectAndInstallment(projectId, installmentNumber);
          });
        }
        break;

      default:
        console.warn('⚠️ Tipo de pagador desconocido para inicialización URL');
    }
  }

  /**
   * 🆕 Seleccionar proyecto (ahora enrollment) e installment después de cargar inscripciones
   */
  private selectProjectAndInstallment(selectionId: number, installmentNumber?: string): void {
    console.log('🎯 Seleccionando proyecto/enrollment e installment:', { selectionId, installmentNumber });

    // Seleccionar la inscripción en el dropdown de "Proyecto"
    this.incomeReceiptForm.patchValue({ projectId: selectionId });
    this.handleGetPaymentPlansByProject({ value: selectionId });

    // Si hay installmentNumber, seleccionarlo después de cargar los payment plans
    if (installmentNumber) {
      this.selectInstallmentWhenReady(installmentNumber);
    }
  }

  /**
   * 🆕 Seleccionar installment cuando los payment plans estén listos
   */
  private selectInstallmentWhenReady(installmentNumber: string): void {
    console.log('⏳ Esperando payment plans para seleccionar installment:', installmentNumber);

    const checkPaymentPlans = () => {
      if (this.paymentsPlans && this.paymentsPlans.length > 0) {
        const matchingPlan = this.paymentsPlans.find((x: any) =>
          x.data.installmentNumber == Number(installmentNumber)
        );

        if (matchingPlan) {
          console.log('✅ Installment encontrado y seleccionado:', matchingPlan);
          this.incomeReceiptForm.patchValue({
            paymentPlanId: matchingPlan.value,
            grossAmount: matchingPlan.data.pendingPayment
          });
        } else {
          console.warn('⚠️ No se encontró installment #' + installmentNumber);
        }
      } else {
        // Reintentar después de un corto período si los payment plans aún no están cargados
        setTimeout(checkPaymentPlans, 100);
      }
    };

    checkPaymentPlans();
  }

  // 🆕 ===== NUEVOS MÉTODOS PARA FLUJO DIFERENCIADO (Principio SRP) =====

  /**
   * Se ejecuta cuando cambia el participante seleccionado
   * Carga proyectos según el tipo de pagador
   */
  onParticipantChange(): void {
    const participantId = this.incomeReceiptForm.get('participantId')?.value;
    console.log('👤 Cambio de participante:', participantId);

    if (!participantId || participantId === 0) {
      this.projects = [];
      this.enrollmentsByStudent = [];
      this.paymentsPlans = [];
      this.incomeProjectBudgets = [];
      this.participantHasEnrollments = null;
      this.participantEnrollmentCheckMessage = '';
      // 🆕 Limpiar empresas filtradas en modo PARTICIPANT_CORPORATE
      if (this.selectedPayerType === this.PayerType.PARTICIPANT_CORPORATE) {
        this.companies = [];
        this.selectedCompanyId = null;
        this.incomeReceiptForm.patchValue({ companyId: null });
      }
      return;
    }

    switch (this.selectedPayerType) {
      case this.PayerType.PARTICIPANT:
        this.loadIndividualEnrollments(participantId).catch(error => {
          console.error('Error cargando inscripciones individuales:', error);
        });
        break;

      case this.PayerType.PARTICIPANT_CORPORATE:
        // 🆕 CORRECCIÓN: Cargar empresas del participante primero
        this.loadCompaniesByParticipant(participantId).catch(error => {
          console.error('Error cargando empresas del participante:', error);
        });
        break;
    }
  }

  /**
   * Se ejecuta cuando cambia la empresa seleccionada
   * Carga proyectos según el tipo de pagador
   */
  onCompanyChange(): void {
    console.log('🏢 Cambio de empresa:', this.selectedCompanyId);

    if (!this.selectedCompanyId) {
      this.projects = [];
      this.enrollmentsByStudent = [];
      this.paymentsPlans = [];
      this.incomeProjectBudgets = [];
      this.companyHasEnrollments = null;
      this.companyEnrollmentCheckMessage = '';
      return;
    }

    switch (this.selectedPayerType) {
      case this.PayerType.COMPANY:
        this.loadCorporateEnrollmentsByCompany(this.selectedCompanyId).catch(error => {
          console.error('Error cargando inscripciones corporativas por empresa:', error);
        });
        break;

      case this.PayerType.PARTICIPANT_CORPORATE:
        // 🆕 CORRECCIÓN: Cargar proyectos filtrados por participante Y empresa
        const participantId = this.incomeReceiptForm.get('participantId')?.value;
        if (participantId && participantId !== 0) {
          console.log('🤝 Cargando proyectos para participante:', participantId, 'y empresa:', this.selectedCompanyId);
          this.loadCorporateEnrollmentsByParticipant(participantId).catch(error => {
            console.error('Error cargando inscripciones corporativas por participante:', error);
          });
        } else {
          console.warn('⚠️ No hay participante seleccionado para cargar proyectos');
          this.toastService.showToast('warning', 'Seleccione primero un participante');
        }
        break;
    }
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractDataArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  /**
   * Cargar inscripciones individuales de un participante
   */
  private loadIndividualEnrollments(participantId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('📚 === CARGANDO INSCRIPCIONES INDIVIDUALES ===');
      console.log('📚 ParticipantId:', participantId);
      this._loadingService.show();

      this._enrollmentService.getEnrollmentsByStudentId(participantId).subscribe({
        next: (response: any) => {
          console.log('📚 === RESPONSE DE INSCRIPCIONES INDIVIDUALES ===');
          console.log('📚 Response completo:', response);
          console.log('📚 Response.data:', response.data);
          console.log('📚 Tipo de response.data:', typeof response.data);
          console.log('📚 Es array?:', Array.isArray(response.data));

          const enrollmentsData = this.extractDataArray(response);
          this.enrollmentsByStudent = enrollmentsData;
          console.log('📚 enrollmentsByStudent asignado:', this.enrollmentsByStudent);

          if (enrollmentsData && enrollmentsData.length > 0) {
            console.log('📚 Primera inscripción detallada:', enrollmentsData[0]);
            console.log('📚 Estructura de la primera inscripción:');
            console.log('📚   - id:', enrollmentsData[0].id);
            console.log('📚   - project.id:', enrollmentsData[0].project?.id);
            console.log('📚   - project.name:', enrollmentsData[0].project?.name);
          }

          // 🆕 Filtrar y mapear solo proyectos válidos (excluir inscripciones reversadas/anuladas)
          this.projects = enrollmentsData
            .filter((enrollment: any) => {
              // Validar que tenga proyecto con id y name válidos
              if (!enrollment?.project?.id || !enrollment?.project?.name) return false;
              // Excluir inscripciones reversadas (status 6 / "Reversed")
              const status = enrollment.status;
              if (status === 6 || status === 'Reversed' || status === 'reversed') return false;
              return true;
            })
            .map((enrollment: any) => {
              const projectName = enrollment.project.name || '';
              const projectId = enrollment.project.projectId ? ` (${enrollment.project.projectId})` : '';
              const mappedProject = {
                label: `${projectName}${projectId} (Inscrip. #${enrollment.id})`,
                value: enrollment.id,
                projectId: enrollment.project.id,
                enrollmentId: enrollment.id,
                // ✅ USAR MONEDA DE LA INSCRIPCIÓN, NO DEL PROYECTO
                enrollmentCurrencyId: enrollment.currencyId,
                enrollmentCurrencyCode: enrollment.currencyCode || 'COP',
                enrollmentCurrencySymbol: enrollment.currencySymbol || '$',
                enrollmentExchangeRate: enrollment.exchangeRateUsed || 1,
                projectData: enrollment.project
              };
              console.log('📚 Mapeo de proyecto:', mappedProject);
              return mappedProject;
            })
            .filter((p: any) => p.label && p.value); // 🆕 Filtrar proyectos sin label o value válidos

          console.log('✅ Proyectos finales mapeados:', this.projects.length, 'elementos');
          this.projects.forEach((p: any, index: number) => {
            console.log(`✅   [${index}] ${p.label} - value: ${p.value}, enrollmentId: ${p.enrollmentId}`);
          });

          // Validar si el participante tiene inscripciones
          if (this.projects.length === 0) {
            this.participantHasEnrollments = false;
            this.participantEnrollmentCheckMessage = 'El estudiante no tiene inscripciones activas, retiradas, aplazadas o canceladas';
            this.toastService.showToast('warning', this.participantEnrollmentCheckMessage);
          } else {
            this.participantHasEnrollments = true;
            this.participantEnrollmentCheckMessage = '';
          }

          this._loadingService.hide();
          resolve();
        },
        error: (error: any) => {
          console.error('❌ === ERROR CARGANDO INSCRIPCIONES INDIVIDUALES ===');
          console.error('❌ Error completo:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);
          this.toastService.showToast('error', 'Error cargando inscripciones del participante');
          this.participantHasEnrollments = false;
          this.participantEnrollmentCheckMessage = 'Error al verificar inscripciones del estudiante';
          this.projects = [];
          this._loadingService.hide();
          reject(error);
        }
      });
    });
  }

  /**
   * Cargar inscripciones corporativas de una empresa
   */
  private loadCorporateEnrollmentsByCompany(companyId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this._loadingService.show();
      this._corporateEnrollmentService.getCorporateEnrollmentsByCompany(companyId).subscribe({
        next: (response: any) => {
          console.log('🏢 Inscripciones corporativas por empresa cargadas:', response.data);
          const corporateEnrollments = this.extractDataArray(response);

          // Validar si la empresa tiene inscripciones corporativas
          if (corporateEnrollments.length === 0) {
            this.companyHasEnrollments = false;
            this.companyEnrollmentCheckMessage = 'La empresa no tiene inscripciones corporativas activas';
            this.toastService.showToast('warning', this.companyEnrollmentCheckMessage);
            this.projects = [];
            this._loadingService.hide();
            resolve();
            return;
          }

          this.companyHasEnrollments = true;
          this.companyEnrollmentCheckMessage = '';

          // 🆕 Filtrar y mapear solo proyectos válidos (excluir anuladas)
          this.projects = corporateEnrollments
            .filter((enrollment: any) => {
              if (!enrollment?.project?.id || !enrollment?.project?.name) return false;
              if (enrollment.isCancelled === true) return false;
              return true;
            })
            .map((enrollment: any) => {
              const projectName = enrollment.project.name || '';
              const projectId = enrollment.project.projectId ? ` (${enrollment.project.projectId})` : '';
              const companyName = enrollment.company?.name ? ` - ${enrollment.company.name}` : '';
              return {
                label: `${projectName}${projectId}${companyName} (Inscrip. #${enrollment.id})`,
                value: enrollment.id,
                projectId: enrollment.project.id,
                corporateEnrollmentId: enrollment.id,
                companyName: enrollment.company?.name,
                // ✅ USAR MONEDA DE LA INSCRIPCIÓN CORPORATIVA, NO DEL PROYECTO
                enrollmentCurrencyId: enrollment.currencyId,
                enrollmentCurrencyCode: enrollment.currencyCode || 'COP',
                enrollmentCurrencySymbol: enrollment.currencySymbol || '$',
                enrollmentExchangeRate: enrollment.exchangeRateUsed || 1,
                projectData: enrollment.project
              };
            })
            .filter((p: any) => p.label && p.value); // 🆕 Filtrar proyectos sin label o value válidos
          console.log('🔍 Proyectos mapeados (empresa):', this.projects.length, 'proyectos válidos');
          this._loadingService.hide();
          resolve();
        },
        error: (error: any) => {
          console.error('❌ Error cargando inscripciones corporativas por empresa:', error);
          this.toastService.showToast('error', 'Error cargando inscripciones corporativas');
          this.companyHasEnrollments = false;
          this.companyEnrollmentCheckMessage = 'Error al verificar inscripciones de la empresa';
          this.projects = [];
          this._loadingService.hide();
          reject(error);
        }
      });
    });
  }

  /**
   * Cargar inscripciones corporativas donde participa un estudiante
   */
  private loadCorporateEnrollmentsByParticipant(participantId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this._loadingService.show();
      this._corporateEnrollmentService.getCorporateEnrollmentsByParticipant(participantId).subscribe({
        next: (response: any) => {
          console.log('🤝 Inscripciones corporativas por participante cargadas:', response.data);
          let corporateEnrollments = this.extractDataArray(response);

          // Si hay empresa seleccionada, filtrar por esa empresa
          if (this.selectedCompanyId) {
            corporateEnrollments = corporateEnrollments.filter(
              (enrollment: any) => enrollment.company?.id === this.selectedCompanyId
            );
          }

          // Validar si hay inscripciones después del filtrado
          if (corporateEnrollments.length === 0) {
            this.participantHasEnrollments = false;
            this.participantEnrollmentCheckMessage = this.selectedCompanyId
              ? 'El estudiante no tiene inscripciones corporativas en la empresa seleccionada'
              : 'El estudiante no tiene inscripciones corporativas';
            this.toastService.showToast('warning', this.participantEnrollmentCheckMessage);
            this.projects = [];
            this._loadingService.hide();
            resolve();
            return;
          }

          this.participantHasEnrollments = true;
          this.participantEnrollmentCheckMessage = '';

          // 🆕 Filtrar y mapear solo proyectos válidos (excluir anuladas)
          this.projects = corporateEnrollments
            .filter((enrollment: any) => {
              if (!enrollment?.project?.id || !enrollment?.project?.name) return false;
              if (enrollment.isCancelled === true) return false;
              return true;
            })
            .map((enrollment: any) => {
              const projectName = enrollment.project.name || '';
              const projectId = enrollment.project.projectId ? ` (${enrollment.project.projectId})` : '';
              const companyName = enrollment.company?.name ? ` - ${enrollment.company.name}` : '';
              return {
                label: `${projectName}${projectId}${companyName} (Inscrip. #${enrollment.id})`,
                value: enrollment.id,
                projectId: enrollment.project.id,
                corporateEnrollmentId: enrollment.id,
                companyName: enrollment.company?.name,
                // ✅ USAR MONEDA DE LA INSCRIPCIÓN CORPORATIVA, NO DEL PROYECTO
                enrollmentCurrencyId: enrollment.currencyId,
                enrollmentCurrencyCode: enrollment.currencyCode || 'COP',
                enrollmentCurrencySymbol: enrollment.currencySymbol || '$',
                enrollmentExchangeRate: enrollment.exchangeRateUsed || 1,
                projectData: enrollment.project
              };
            })
            .filter((p: any) => p.label && p.value); // 🆕 Filtrar proyectos sin label o value válidos
          console.log('🔍 Proyectos mapeados (participante corporativo):', this.projects.length, 'proyectos válidos');
          this._loadingService.hide();
          resolve();
        },
        error: (error: any) => {
          console.error('❌ Error cargando inscripciones corporativas por participante:', error);
          this.toastService.showToast('error', 'Error cargando inscripciones corporativas del participante');
          this.participantHasEnrollments = false;
          this.participantEnrollmentCheckMessage = 'Error al verificar inscripciones corporativas del estudiante';
          this.projects = [];
          this._loadingService.hide();
          reject(error);
        }
      });
    });
  }

  /**
   * 🆕 Cargar únicamente las empresas en las que el participante tiene inscripciones corporativas
   * Este método se usa cuando el tipo de pagador es PARTICIPANT_CORPORATE
   */
  private loadCompaniesByParticipant(participantId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('🏢 === CARGANDO EMPRESAS DEL PARTICIPANTE ===');
      console.log('🏢 ParticipantId:', participantId);
      this._loadingService.show();

      this._corporateEnrollmentService.getCorporateEnrollmentsByParticipant(participantId).subscribe({
        next: (response: any) => {
          console.log('🏢 Response de inscripciones corporativas:', response);
          const corporateEnrollments = this.extractDataArray(response);

          if (corporateEnrollments.length === 0) {
            console.warn('⚠️ El participante no tiene inscripciones corporativas');
            this.companies = [];
            this.participantHasEnrollments = false;
            this.participantEnrollmentCheckMessage = 'El estudiante no tiene inscripciones corporativas en ninguna empresa';
            this.toastService.showToast('warning', this.participantEnrollmentCheckMessage);
            this._loadingService.hide();
            resolve();
            return;
          }

          // 🎯 Extraer empresas únicas de las inscripciones
          const uniqueCompaniesMap = new Map();
          corporateEnrollments.forEach((enrollment: any) => {
            if (enrollment.company && enrollment.company.id) {
              uniqueCompaniesMap.set(enrollment.company.id, enrollment.company);
            }
          });

          // 🎯 Mapear empresas al formato del dropdown
          this.companies = Array.from(uniqueCompaniesMap.values()).map((company: any) => ({
            label: `${company.name} (${company.documentId})`,
            value: company.id,
            data: company
          }));

          console.log('✅ Empresas únicas del participante:', this.companies);
          console.log(`✅ Total de empresas encontradas: ${this.companies.length}`);

          if (this.companies.length > 0) {
            this.toastService.showToast('success', `${this.companies.length} empresa(s) encontrada(s) para este participante`);
          }

          this.participantHasEnrollments = true;
          this.participantEnrollmentCheckMessage = '';

          this._loadingService.hide();
          resolve();
        },
        error: (error: any) => {
          console.error('❌ Error cargando empresas del participante:', error);
          this.toastService.showToast('error', 'Error cargando empresas del participante');
          this.participantHasEnrollments = false;
          this.participantEnrollmentCheckMessage = 'Error al verificar inscripciones corporativas del estudiante';
          this.companies = [];
          this._loadingService.hide();
          reject(error);
        }
      });
    });
  }

  /**
   * 🎯 Determina el ParticipantId correcto según el tipo de pagador
   */
  getCorrectParticipantId(payerType: string, participantId: any): number | null {
    switch (payerType) {
      case 'PARTICIPANT':
        // Pago individual: requiere participantId válido
        return participantId && participantId > 0 ? Number(participantId) : null;

      case 'COMPANY':
        // Pago de empresa: NO requiere participantId
        return null;

      case 'PARTICIPANT_CORPORATE':
        // Pago de participante de empresa: requiere participantId válido
        return participantId && participantId > 0 ? Number(participantId) : null;

      default:
        console.warn('⚠️ PayerType desconocido:', payerType);
        return null;
    }
  }

  /**
   * 🎯 Determina el CompanyId correcto según el tipo de pagador
   */
  getCorrectCompanyId(payerType: string, companyId: any): number | null {
    switch (payerType) {
      case 'PARTICIPANT':
        // Pago individual: NO requiere companyId
        return null;

      case 'COMPANY':
        // Pago de empresa: requiere companyId válido
        return companyId && companyId > 0 ? Number(companyId) : null;

      case 'PARTICIPANT_CORPORATE':
        // Pago de participante de empresa: requiere companyId válido
        return companyId && companyId > 0 ? Number(companyId) : null;

      default:
        console.warn('⚠️ PayerType desconocido:', payerType);
        return null;
    }
  }

  /**
   * 🎯 Determina el PaymentPlanId correcto según el tipo de pagador
   * Solo para pagos individuales (PARTICIPANT)
   */
  getCorrectPaymentPlanId(payerType: string, paymentPlanId: any): number | null {
    switch (payerType) {
      case 'PARTICIPANT':
        // Pago individual: usa PaymentPlanId del formulario
        return paymentPlanId && paymentPlanId > 0 ? Number(paymentPlanId) : null;

      case 'COMPANY':
      case 'PARTICIPANT_CORPORATE':
        // Pagos corporativos: NO usan PaymentPlanId
        return null;

      default:
        console.warn('⚠️ PayerType desconocido para PaymentPlanId:', payerType);
        return null;
    }
  }

  /**
   * 🎯 Determina el CorporatePaymentPlanId correcto según el tipo de pagador
   * Solo para pagos corporativos (COMPANY y PARTICIPANT_CORPORATE)
   */
  getCorrectCorporatePaymentPlanId(payerType: string, paymentPlanId: any): number | null {
    switch (payerType) {
      case 'PARTICIPANT':
        // Pago individual: NO usa CorporatePaymentPlanId
        return null;

      case 'COMPANY':
      case 'PARTICIPANT_CORPORATE':
        // Pagos corporativos: el campo paymentPlanId del formulario contiene el CorporatePaymentPlanId
        return paymentPlanId && paymentPlanId > 0 ? Number(paymentPlanId) : null;

      default:
        console.warn('⚠️ PayerType desconocido para CorporatePaymentPlanId:', payerType);
        return null;
    }
  }

  /**
   * 🆕 Determina si un método de pago requiere generación de link
   * Reemplaza el hardcode del ID == 3
   */
  private shouldGeneratePaymentLink(): boolean {
    const paymentMethodId = this.incomeReceiptForm.get('paymentMethodId')?.value;

    if (!paymentMethodId || !this.filteredPaymentMethods) {
      return false;
    }

    const selectedPaymentMethod = this.filteredPaymentMethods.find(
      (method: any) => method.value === paymentMethodId
    );

    if (!selectedPaymentMethod?.data) {
      console.warn('⚠️ No se encontró información del método de pago:', paymentMethodId);
      return false;
    }

    const { isPaymentGateway, isIntegrated, requiresLinkGeneration } = selectedPaymentMethod.data;

    console.log('🔍 Evaluando método de pago:', {
      paymentMethodId,
      name: selectedPaymentMethod.label,
      isPaymentGateway,
      isIntegrated,
      requiresLinkGeneration
    });

    // Solo generar link si es gateway, está integrado y requiere generación de link
    return isPaymentGateway && isIntegrated && requiresLinkGeneration;
  }

  // === MÉTODOS DE CANCELACIÓN Y NAVEGACIÓN ===

  /**
   * Maneja el botón cancelar con confirmación
   */
  async onCancel(): Promise<void> {
    await this.handleFormCancel('/dashboard/income-receipts');
  }

  /**
   * Maneja la confirmación del modal
   */
  onConfirmationModalConfirmed(): void {
    this.onModalConfirmed();
  }

  /**
   * Maneja la cancelación del modal
   */
  onConfirmationModalCancelled(): void {
    this.onModalCancelled();
  }

  /**
   * Maneja el cierre del modal
   */
  onConfirmationModalClosed(): void {
    this.onModalClosed();
  }

  /**
   * Confirmación antes de generar payment link
   */
  async confirmGeneratePaymentLink(): Promise<boolean> {
    const amount = this.incomeReceiptForm.get('grossAmount')?.value || 0;
    return await this.confirmationService.incomeReceipt.generatePaymentLink(amount);
  }

  // 🆕 MÉTODO PARA CARGAR COMPROBANTE EN MODO EDICIÓN
  /**
   * Carga los datos de un comprobante existente para pre-poblar el formulario en modo edición.
   * El backend devuelve objetos anidados (receipt.project, receipt.bank, etc.),
   * por lo que los IDs se extraen de esos objetos: receipt.project?.id, receipt.bank?.id, etc.
   */
  private loadReceiptForEditing(receiptId: number): void {
    this._loadingService.show();

    // 1️⃣ Primero cargar catálogos iniciales (bancos, métodos de pago, etc.)
    this.loadInitialData().pipe(
      // 2️⃣ Luego obtener datos del comprobante
      switchMap(() => this._incomeReceiptService.getById(receiptId, true)),
      // 3️⃣ Procesar datos del comprobante y cargar dependencias (proyectos, matrículas)
      switchMap((response: any) => {
        const receipt = response.data;
        console.log('📝 Comprobante cargado para edición:', receipt);

        // ✅ Extraer IDs desde objetos anidados (el backend devuelve objetos, no IDs planos)
        const participantId = receipt.participant?.id;
        const companyId = receipt.company?.id;
        const projectId = receipt.project?.id;
        const bankId = receipt.bank?.id;
        const paymentMethodId = receipt.paymentMethod?.id;
        const paymentPlanId = receipt.paymentPlan?.id;

        console.log('📝 IDs extraídos:', { participantId, companyId, projectId, bankId, paymentMethodId, paymentPlanId });

        // Guardar referencia temporal con IDs ya extraídos para usar en el siguiente paso
        this.tempReceiptData = {
          ...receipt,
          // Aplanar IDs para uso en patchValue
          _participantId: participantId,
          _companyId: companyId,
          _projectId: projectId,
          _bankId: bankId,
          _paymentMethodId: paymentMethodId,
          _paymentPlanId: paymentPlanId
        };

        // Establecer tipo de pagador
        this.selectedPayerType = receipt.payerType || 'PARTICIPANT';
        if (companyId) {
          this.selectedCompanyId = companyId;
        }

        // Cargar proyectos según el tipo de pagador y IDs
        const payerType = receipt.payerType || 'PARTICIPANT';

        // Retornar promesa de carga de proyectos
        if (payerType === 'PARTICIPANT' && participantId) {
          return this.loadIndividualEnrollments(participantId);
        } else if (payerType === 'COMPANY' && companyId) {
          return this.loadCorporateEnrollmentsByCompany(companyId);
        } else if (payerType === 'PARTICIPANT_CORPORATE' && participantId) {
          return this.loadCompaniesByParticipant(participantId).then(() =>
            this.loadCorporateEnrollmentsByParticipant(participantId)
          );
        }

        return new Promise<void>((resolve) => resolve());
      })
    ).subscribe({
      next: () => {
        const receipt = this.tempReceiptData;

        // 4️⃣ Pre-poblar el formulario con IDs aplanados
        this.incomeReceiptForm.patchValue({
          payerType: receipt.payerType || 'PARTICIPANT',
          participantId: receipt._participantId || 0,
          companyId: receipt._companyId || null,
          projectId: receipt._projectId,
          projectBudgetId: receipt.projectBudgetId || null,
          paymentMethodId: receipt._paymentMethodId,
          bankId: receipt._bankId || '',
          grossAmount: receipt.grossAmount,
          currencyId: receipt.currencyId || 1,
          exchangeRate: receipt.exchangeRate || 0,
          referenceNumber: receipt.referenceNumber || '',
          concept: receipt.concept || '',
          observations: receipt.observations || '',
          transactionDate: receipt.transactionDate ? new Date(receipt.transactionDate) : new Date(),
          paymentPlanId: receipt._paymentPlanId || 0
        });

        console.log('✅ Formulario patcheado con:', this.incomeReceiptForm.value);

        // 5️⃣ Cargar dependencias del proyecto (Planes de Pago y Categorías de Ingreso)
        if (receipt._projectId) {
          this.handleGetPaymentPlansByProject({ value: receipt._projectId });
        }

        // Cargar fee details si existen
        if (receipt.feeDetails && receipt.feeDetails.length > 0) {
          const feeArray = this.incomeReceiptForm.get('feeDetails') as FormArray;
          feeArray.clear();
          receipt.feeDetails.forEach((fee: any) => {
            feeArray.push(this.fb.group({
              feeTypeId: [fee.feeTypeId],
              requestAmount: [fee.amount?.toString() || ''],
              description: [fee.description || '']
            }));
          });
        }

        this._loadingService.hide();

        // Avanzar al step 2 directamente en modo edición
        this.step = 2;

        this.toastService.showToast('warning', `Editando comprobante #${receiptId}. Modifique los campos necesarios.`);
      },
      error: (err: any) => {
        this._loadingService.hide();
        console.error('❌ Error cargando comprobante para edición:', err);
        this.toastService.showToast('error', 'Error al cargar el comprobante para edición');
        this.router.navigate(['/income-receipts']);
      }
    });
  }

  // Variable temporal para almacenar datos entre pasos del pipe
  private tempReceiptData: any;
}
